import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useProfileConfig, useUpdateProfile, WireProfile } from "./service";

/**
 * Workflow-level Save. One coherent Save affordance orchestrates the four configuration sections
 * (Metadata Source, Field Mappings, Applies To, Schedule) that each own a slice of the same
 * profile. It does NOT rewrite persistence: every section still contributes exactly the partial
 * patch it produced before, and the whole set merges into a SINGLE `PUT /profiles/:id` request —
 * so the save is one real round-trip, not four, and not a fake "transaction" wrapped around four
 * calls. Sections register their live dirty-state + patch builder here; the header Save button and
 * the CHANGES sidebar panel read from the same registry.
 */

export type SectionKey = "status" | "source" | "fields" | "target" | "schedule";

/** What a section contributes to the merged PUT. Everything except `source` replaces its top-level
 *  key wholesale; `source` may be a partial delta (only the sub-keys that section owns) — the
 *  controller backfills the rest from current config so a partial never wipes a sibling's edit. */
export type PatchContribution = Omit<Partial<WireProfile>, "source"> & {
  source?: Partial<WireProfile["source"]>;
};

interface SectionLive {
  order: number;
  label: string;
  getPatch: () => PatchContribution;
  /** Optional client-side gate — a non-null string blocks the save and is shown against the section. */
  validate?: () => string | null;
}

interface SectionMeta {
  order: number;
  label: string;
}

export interface SaveResult {
  ok: boolean;
  message?: string;
  error?: string;
}

interface SaveContextValue {
  registerLive: (key: SectionKey, live: SectionLive) => void;
  setDirty: (key: SectionKey, meta: SectionMeta | null) => void;
  dirtySections: (SectionMeta & { key: SectionKey })[];
  isDirty: boolean;
  isSaving: boolean;
  save: () => void;
  result: SaveResult | null;
  errorSection: SectionKey | null;
  clearResult: () => void;
}

const noop = () => undefined;
const SaveContext = createContext<SaveContextValue>({
  registerLive: noop,
  setDirty: noop,
  dirtySections: [],
  isDirty: false,
  isSaving: false,
  save: noop,
  result: null,
  errorSection: null,
  clearResult: noop,
});

/** Merge each section's partial into one PUT body. Top-level keys are replaced wholesale (matching
 *  the wire contract), EXCEPT `source`, whose contributing sections send only their own sub-keys —
 *  those accumulate onto the current source so the merged source is always complete. */
export function mergePatches(
  patches: PatchContribution[],
  baseSource: WireProfile["source"]
): Partial<WireProfile> {
  const out: Partial<WireProfile> = {};
  let sourceDelta: Record<string, unknown> | null = null;
  for (const p of patches) {
    for (const [k, v] of Object.entries(p)) {
      if (k === "source") {
        sourceDelta = { ...(sourceDelta ?? {}), ...(v as object) };
      } else {
        (out as Record<string, unknown>)[k] = v;
      }
    }
  }
  if (sourceDelta) {
    out.source = { ...baseSource, ...sourceDelta } as WireProfile["source"];
  }
  return out;
}

export function SaveProvider({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const cfg = useProfileConfig(id);
  const update = useUpdateProfile(id);
  const liveRef = useRef<Partial<Record<SectionKey, SectionLive>>>({});
  const [dirtyMap, setDirtyMap] = useState<
    Partial<Record<SectionKey, SectionMeta>>
  >({});
  const [result, setResult] = useState<SaveResult | null>(null);
  const [errorSection, setErrorSection] = useState<SectionKey | null>(null);

  const registerLive = useCallback((key: SectionKey, live: SectionLive) => {
    liveRef.current[key] = live;
  }, []);

  const setDirty = useCallback((key: SectionKey, meta: SectionMeta | null) => {
    setDirtyMap((prev) => {
      const cur = prev[key];
      if (!meta) {
        if (!cur) {
          return prev;
        }
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (cur && cur.order === meta.order && cur.label === meta.label) {
        return prev;
      }
      return { ...prev, [key]: meta };
    });
  }, []);

  const dirtySections = useMemo(
    () =>
      (Object.entries(dirtyMap) as [SectionKey, SectionMeta][])
        .map(([key, meta]) => ({ key, ...meta }))
        .sort((a, b) => a.order - b.order),
    [dirtyMap]
  );

  const clearResult = useCallback(() => setResult(null), []);

  const save = useCallback(async () => {
    if (!cfg.data || update.isLoading || dirtySections.length === 0) {
      return;
    }
    setResult(null);
    setErrorSection(null);
    // Validate every dirty section first — surface the first problem against its section.
    for (const s of dirtySections) {
      const err = liveRef.current[s.key]?.validate?.();
      if (err) {
        setErrorSection(s.key);
        setResult({ ok: false, error: err });
        return;
      }
    }
    const patches = dirtySections
      .map((s) => liveRef.current[s.key]?.getPatch())
      .filter((p): p is PatchContribution => Boolean(p));
    const merged = mergePatches(patches, cfg.data.source);
    try {
      const saved = (await update.mutateAsync(merged)) as
        | (WireProfile & {
            scheduleSync?: { ok: boolean; message?: string; error?: string };
          })
        | undefined;
      const sync = saved?.scheduleSync;
      // A missing unattended schedule (manual/draft/paused/non-cadence) is NORMAL, not a status to
      // narrate in the action row — the happy path always reads a plain "Saved.". A genuine
      // reconcile FAILURE is surfaced separately (errorSection "schedule" → shown in the Schedule
      // rail, never as a paragraph between Run Now and Saved).
      if (sync && !sync.ok) {
        setErrorSection("schedule");
        setResult({
          ok: false,
          error: `Saved, but the schedule could not be synced: ${
            sync.error ?? "unknown error"
          }`,
        });
      } else {
        setResult({ ok: true, message: "Saved." });
      }
    } catch (e) {
      // `e.message` guard: a non-JSON proxy error carries no message, and an empty string would
      // otherwise render as a blank error.
      setResult({
        ok: false,
        error:
          e instanceof Error && e.message
            ? e.message.replace(/^\[\d+\]\s*/, "")
            : "Could not save changes.",
      });
    }
  }, [cfg.data, update, dirtySections]);

  const value = useMemo<SaveContextValue>(
    () => ({
      registerLive,
      setDirty,
      dirtySections,
      isDirty: dirtySections.length > 0,
      isSaving: update.isLoading,
      save,
      result,
      errorSection,
      clearResult,
    }),
    [
      registerLive,
      setDirty,
      dirtySections,
      update.isLoading,
      save,
      result,
      errorSection,
      clearResult,
    ]
  );

  return <SaveContext.Provider value={value}>{children}</SaveContext.Provider>;
}

export function useSaveContext(): SaveContextValue {
  return useContext(SaveContext);
}

/**
 * Section-side hook. Called from each editor on every render with its current dirty-state + patch
 * builder. The live closures are stored synchronously (so a save always reads the latest edits);
 * the serialisable dirty flag flows through state (so the Save button / CHANGES panel re-render).
 */
export function useSaveSection(
  key: SectionKey,
  opts: {
    order: number;
    label: string;
    dirty: boolean;
    getPatch: () => PatchContribution;
    validate?: () => string | null;
  }
): void {
  const { registerLive, setDirty } = useSaveContext();
  const { order, label, dirty, getPatch, validate } = opts;
  // Store the live closures every render — never stale at save time.
  registerLive(key, { order, label, getPatch, validate });
  useEffect(() => {
    setDirty(key, dirty ? { order, label } : null);
  }, [key, dirty, order, label, setDirty]);
  useEffect(() => () => setDirty(key, null), [key, setDirty]);
}
